import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Container,
  Chip,
  ButtonGroup,
  Divider,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { CheckCircle, Close } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import {
  fetchBranches,
  fetchProducts,
  registerBranchInit,
  unregisterBranch,
  fetchStock,
} from "../utils/api";
import ProductForm from "./ProductForm";
import StockForm from "./StockForm";
import BranchForm from "./BranchForm";
import OverlayLoader from "../components/OverlayLoader";

export default function BranchCards() {
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerDialog, setRegisterDialog] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenSnackbar = () => setSnackbarOpen(true);
  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleOpenRegisterDialog = () => setRegisterDialogOpen(true);
  const handleCloseRegisterDialog = () => setRegisterDialogOpen(false);

  useEffect(() => {
    fetchBranchesData("", true);
  }, []);

  const handleRegisterDeviceConfirm = (branch) => {
    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">
          Proceed with device registration?
        </DialogTitle>
        <DialogContent>
          This action will generate and display an OTP that will expire in 1
          minute.
          <br />
          Send the OTP to the user holding the device.
          <br />
          Inform the user to go to the login page and enter the OTP.
          <br />
          The user will automatically be redirected to the login form if the OTP
          is valid.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegisterDialog}>Cancel</Button>
          <Button onClick={() => handleRegisterDevice(branch)} autoFocus>
            Continue
          </Button>
        </DialogActions>
      </>
    );

    handleOpenRegisterDialog();
  };

  const handleRegisterDevice = async (branch) => {
    try {
      const response = await registerBranchInit({ br: branch });
      if (response.data?.data?.otp) {
        setRegisterDialog(
          <>
            <DialogTitle id="alert-dialog-title">
              Device registration is now open.
            </DialogTitle>
            <DialogContent>
              Send the OTP to the user holding the device.
              <br />
              Inform the user to go to the login page and enter the OTP.
              <br />
              The user will automatically be redirected to the login page if the
              OTP is valid.
              <br />
              Close this popup once done.
              <br />
              <br />
              Please see below OTP (expires in 1 minute):
              <br />
              <Typography variant="h6">{response.data?.data?.otp}</Typography>
              <br />
              <Typography variant="caption">
                If you encountered an issue, please try again later.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  handleCloseRegisterDialog();
                  fetchBranchesData(branch, false);
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        );
      } else {
        setSnackbarMessage(response.data.message || "Failed");
        setSnackbarSeverity("error");
        handleOpenSnackbar();
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage(
        error?.response?.data?.message ||
          "An error occurred. Please try again later."
      );
      setSnackbarSeverity("error");
      handleOpenSnackbar();
    }
  };

  const handleUnregisterDeviceConfirm = (branch) => {
    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to unregister the device?
        </DialogTitle>
        <DialogContent>
          After this, the device can no longer login to this branch.
          <br />
          This action will not auto logout the user so kindly logout manually.
          <br />
          You can always reregister the device.
          <br />
          <br />
          <Typography variant="caption">
            If you encountered an issue, please try again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegisterDialog}>Cancel</Button>
          <Button onClick={() => handleUnregisterDevice(branch)} autoFocus>
            Continue
          </Button>
        </DialogActions>
      </>
    );

    handleOpenRegisterDialog();
  };

  const handleUnregisterDevice = async (branch) => {
    try {
      const response = await unregisterBranch({ branch: branch });
      if (response.status === 200) {
        setSnackbarMessage(response.data.message || "Successful");
        setSnackbarSeverity("success");
        handleOpenSnackbar();
        handleCloseRegisterDialog();
        fetchBranchesData(branch, false);
      } else {
        setSnackbarMessage(response.data.message || "Failed");
        setSnackbarSeverity("error");
        handleOpenSnackbar();
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage(
        error?.response?.data?.message ||
          "An error occurred. Please try again later."
      );
      setSnackbarSeverity("error");
      handleOpenSnackbar();
    }
  };

  const handleAddProduct = (branch) => {
    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">Add Product</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseRegisterDialog}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent>
          <ProductForm
            branch={branch}
            done={() => {
              handleCloseRegisterDialog();
              fetchProductsData(branch);
            }}
            setSnackbarMessage={setSnackbarMessage}
            setSnackbarSeverity={setSnackbarSeverity}
            handleOpenSnackbar={handleOpenSnackbar}
          ></ProductForm>
        </DialogContent>
      </>
    );
    handleOpenRegisterDialog();
  };

  const handleEditProduct = (product) => {
    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">Edit Product</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseRegisterDialog}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent>
          <ProductForm
            product={product}
            done={() => {
              handleCloseRegisterDialog();
              fetchProductsData(product.br._id);
            }}
            setSnackbarMessage={setSnackbarMessage}
            setSnackbarSeverity={setSnackbarSeverity}
            handleOpenSnackbar={handleOpenSnackbar}
          ></ProductForm>
        </DialogContent>
      </>
    );
    handleOpenRegisterDialog();
  };

  const handleAddStock = (product) => {
    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">Add Stock</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseRegisterDialog}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent>
          <StockForm
            product={product}
            done={() => {
              handleCloseRegisterDialog();
              fetchProductsData(product.br._id);
            }}
            setSnackbarMessage={setSnackbarMessage}
            setSnackbarSeverity={setSnackbarSeverity}
            handleOpenSnackbar={handleOpenSnackbar}
          ></StockForm>
        </DialogContent>
      </>
    );
    handleOpenRegisterDialog();
  };

  const handleViewStockHistory = async (product) => {
    setIsLoading(true);

    const stockHistory = await fetchStockHistoryByProduct(product);

    setIsLoading(false);

    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">Stock History</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseRegisterDialog}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent>
          <Alert severity="info" variant="outlined">
            Showing only the most recent 100 entries.
          </Alert>
          <TableContainer sx={{ pt: 1, pb: 3 }}>
            <Table
              size="small"
              sx={{ minWidth: 300 }}
              aria-label="simple table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>New Stock</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!stockHistory.length && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: "center" }}>
                      No records available
                    </TableCell>
                  </TableRow>
                )}
                {stockHistory.length > 0 &&
                  stockHistory.map((row) => {
                    return (
                      <TableRow key={row._id}>
                        <TableCell>
                          {new Date(row.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>{row.qty}</TableCell>
                        <TableCell>{row?.nqty ?? "-"}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </>
    );
    handleOpenRegisterDialog();
  };

  const handleAddBranch = () => {
    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">Add Branch</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseRegisterDialog}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent>
          <BranchForm
            done={() => {
              handleCloseRegisterDialog();
              fetchBranchesData("", false);
            }}
            setSnackbarMessage={setSnackbarMessage}
            setSnackbarSeverity={setSnackbarSeverity}
            handleOpenSnackbar={handleOpenSnackbar}
          ></BranchForm>
        </DialogContent>
      </>
    );
    handleOpenRegisterDialog();
  };

  const fetchBranchesData = async (branch, needFetchProducts) => {
    try {
      setIsLoading(true);

      const data = await fetchBranches();
      setBranches(data.data);

      setIsLoading(false);

      if (needFetchProducts) {
        fetchProductsData(
          branch ? data.data.filter((b) => b._id === branch) : data.data
        );
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
    }
  };

  const fetchProductsData = async (branches) => {
    try {
      if (!Array.isArray(branches)) {
        branches = branches ? [{ _id: branches }] : [];
      }

      if (branches.length === 0) return;

      setIsLoading(true);

      const productsData = [];

      for (const branch of branches) {
        const products = await fetchProducts({ branch: branch._id });
        productsData.push(...products.data);
      }

      setProducts((prevProducts) => {
        const updatedBranchIds = new Set(branches.map((b) => b._id));

        const filteredProducts = prevProducts.filter(
          (p) => !updatedBranchIds.has(p.br._id)
        );

        return [...filteredProducts, ...productsData];
      });

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
    }
  };

  const fetchStockHistoryByProduct = async (product) => {
    try {
      const data = await fetchStock({ product: product._id });
      return data.data;
    } catch (error) {
      console.error("Error fetching stock history:", error);
      return [];
    } finally {
    }
  };

  return (
    <>
      <Container maxWidth="sm" sx={{ padding: 2 }}>
        <Stack spacing={2}>
          {branches.map((branch, index) => (
            <Card elevation={3} key={index}>
              <CardContent>
                {/* Header */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">
                    {branch.nm}{" "}
                    {branch.di && (
                      <Chip
                        color="success"
                        label="Device"
                        icon={<CheckCircle />}
                        size="small"
                        variant="outlined"
                        sx={{ border: "none" }}
                      />
                    )}
                  </Typography>
                  <Box>
                    {branch.di ? (
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() =>
                          handleUnregisterDeviceConfirm(branch._id)
                        }
                      >
                        Unregister device
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => handleRegisterDeviceConfirm(branch._id)}
                      >
                        Register device
                      </Button>
                    )}
                  </Box>
                </Box>
                <Typography variant="body6">
                  Gas Usage: {branch.gs || 0}{" "}
                </Typography>
                <Typography
                  variant="caption"
                  color={
                    (branch.gs || 0) >= 230
                      ? (branch.gs || 0) >= 220
                        ? "error.main"
                        : "warning.main"
                      : "text.primary"
                  }
                >
                  {(branch.gs || 0) >= 220 ? "Low gas" : ""}
                </Typography>
              </CardContent>
              <Divider />
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table
                    size="small"
                    sx={{ minWidth: 300 }}
                    aria-label="simple table"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Products</TableCell>
                        <TableCell align="right" sx={{ minWidth: 80 }}>
                          Stock
                        </TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            aria-label="add"
                            onClick={() => handleAddProduct(branch._id)}
                          >
                            <AddIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((row) => {
                        if (row.br._id === branch._id) {
                          return (
                            <TableRow key={row._id}>
                              <TableCell component="th" scope="row">
                                {row.nm}
                                <br />
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  {row.cat === 1
                                    ? "Detergent"
                                    : row.cat === 2
                                    ? "Fabcon"
                                    : row.cat === 3
                                    ? "Bleach"
                                    : ""}
                                </Typography>{" "}
                                <Typography
                                  variant="caption"
                                  color={
                                    (row.stocks || 0) <= 5
                                      ? (row.stocks || 0) === 0
                                        ? "error.main"
                                        : "warning.main"
                                      : "text.primary"
                                  }
                                >
                                  {(row.stocks || 0) <= 5
                                    ? (row.stocks || 0) === 0
                                      ? "Out of stock"
                                      : "Low stock"
                                    : ""}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {(row.stocks || 0)}{" "}
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleAddStock(row)}
                                  title="Add Stock"
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                              <TableCell>P{row.price}</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditProduct(row)}
                                  title="Edit Product"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewStockHistory(row)}
                                  title="View Stock History"
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
          <Card elevation={3}>
            <CardContent sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                size="small"
                sx={{ ml: 1 }}
                onClick={() => handleAddBranch()}
              >
                Add branch
              </Button>
            </CardContent>
          </Card>
        </Stack>
      </Container>
      <Dialog
        open={registerDialogOpen}
        onClose={handleCloseRegisterDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {registerDialog}
      </Dialog>
      <OverlayLoader open={isLoading} />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
